import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Chip,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  Email,
  Phone,
  Person,
  Groups,
  Language,
  Description,
  Download,
  Event,
  Folder,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface BoardMember {
  id: string;
  club_id: string;
  title: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  term_expires: string | null;
  display_order: number;
}

interface ClubDocument {
  id: string;
  club_id: string;
  title: string;
  description: string | null;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  category: string | null;
  is_public: boolean;
  created_at: string;
}

interface ClubWithInfo {
  club_id: string;
  club_name: string;
  members: BoardMember[];
  documents: ClubDocument[];
  logoUrl: string | null;
}

interface ClubBoardViewProps {
  clubIds: string[];
  clubNames?: Record<string, string>;
}

const ClubBoardView: React.FC<ClubBoardViewProps> = ({ clubIds, clubNames = {} }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clubsWithInfo, setClubsWithInfo] = useState<ClubWithInfo[]>([]);

  // Cleanup logo URLs when component unmounts
  useEffect(() => {
    return () => {
      clubsWithInfo.forEach((club) => {
        if (club.logoUrl) {
          URL.revokeObjectURL(club.logoUrl);
        }
      });
    };
  }, [clubsWithInfo]);

  useEffect(() => {
    const fetchClubData = async () => {
      if (clubIds.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const results: ClubWithInfo[] = [];

        for (const clubId of clubIds) {
          const clubData: ClubWithInfo = {
            club_id: clubId,
            club_name: clubNames[clubId] || 'Club',
            members: [],
            documents: [],
            logoUrl: null,
          };

          // Fetch board members
          try {
            const membersResponse = await api.get(`/clubs/${clubId}/board-members`);
            if (membersResponse.data.items && membersResponse.data.items.length > 0) {
              clubData.members = membersResponse.data.items;
            }
          } catch (err) {
            console.log(`No board members for club ${clubId} or access denied`);
          }

          // Fetch documents
          try {
            const docsResponse = await api.get(`/clubs/${clubId}/documents`);
            if (docsResponse.data.items && docsResponse.data.items.length > 0) {
              clubData.documents = docsResponse.data.items;
            }
          } catch (err) {
            console.log(`No documents for club ${clubId} or access denied`);
          }

          // Fetch club logo
          try {
            const logoResponse = await api.get(`/clubs/${clubId}/logo`, {
              responseType: 'blob'
            });
            if (logoResponse.data) {
              clubData.logoUrl = URL.createObjectURL(logoResponse.data);
            }
          } catch (err) {
            // Logo is optional, ignore 404 errors
            console.log(`No logo for club ${clubId}`);
          }

          // Only add if we have some meaningful data
          if (clubData.members.length > 0 || clubData.documents.length > 0) {
            results.push(clubData);
          }
        }

        setClubsWithInfo(results);
      } catch (err: any) {
        console.error('Failed to fetch club data:', err);
        setError(t('boardMembers.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchClubData();
  }, [clubIds, clubNames, t]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(i18n.language);
  };

  const isExpired = (dateString: string | null) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadDocument = async (clubId: string, documentId: string, fileName: string) => {
    try {
      const response = await api.get(`/clubs/${clubId}/documents/${documentId}/download`);
      if (response.data.download_url) {
        // Construct full URL using the API base URL
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const baseUrl = apiBaseUrl.replace('/api/v1', '');
        const fullUrl = `${baseUrl}${response.data.download_url}`;
        window.open(fullUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const getCategoryLabel = (category: string | null) => {
    if (!category) return t('clubDocuments.categories.other');
    return t(`clubDocuments.categories.${category}`, { defaultValue: category });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (clubsWithInfo.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Groups color="primary" />
        {t('boardMembers.pilotView.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('boardMembers.pilotView.description')}
      </Typography>

      {clubsWithInfo.map((club) => (
        <Accordion key={club.club_id} defaultExpanded={clubsWithInfo.length === 1}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {club.logoUrl && (
                <Avatar
                  src={club.logoUrl}
                  alt={club.club_name}
                  variant="rounded"
                  sx={{ width: 40, height: 40 }}
                />
              )}
              <Typography variant="subtitle1" fontWeight="medium">
                {club.club_name}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Board Members */}
            {club.members.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" />
                  {t('boardMembers.title')}
                </Typography>
                <List disablePadding>
                  {club.members.map((member, index) => (
                    <React.Fragment key={member.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {member.title}
                              </Typography>
                              <Typography variant="body2">
                                - {member.first_name} {member.last_name}
                              </Typography>
                              {member.term_expires && (
                                <Chip
                                  icon={<Event />}
                                  label={formatDate(member.term_expires)}
                                  size="small"
                                  color={isExpired(member.term_expires) ? 'error' : 'default'}
                                  variant={isExpired(member.term_expires) ? 'filled' : 'outlined'}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              {member.email && (
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Email fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    component="a"
                                    href={`mailto:${member.email}`}
                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    {member.email}
                                  </Typography>
                                </Box>
                              )}
                              {member.phone && (
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Phone fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    component="a"
                                    href={`tel:${member.phone}`}
                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    {member.phone}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}

            {/* Club Documents */}
            {club.documents.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Folder fontSize="small" />
                  {t('clubDocuments.title')}
                </Typography>
                <List disablePadding>
                  {club.documents.map((doc, index) => (
                    <React.Fragment key={doc.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem
                        sx={{ px: 0 }}
                        secondaryAction={
                          <Button
                            size="small"
                            startIcon={<Download />}
                            onClick={() => handleDownloadDocument(club.club_id, doc.id, doc.file_name)}
                          >
                            {t('clubDocuments.download')}
                          </Button>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'secondary.light' }}>
                            <Description />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body1">
                                {doc.title}
                              </Typography>
                              {doc.category && (
                                <Chip
                                  label={getCategoryLabel(doc.category)}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              {doc.description && (
                                <Typography variant="body2" color="text.secondary" component="span">
                                  {doc.description}
                                </Typography>
                              )}
                              {doc.file_size && (
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {formatFileSize(doc.file_size)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default ClubBoardView;
